import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "checkbox", 
    "pupilRow", 
    "enrolledList", 
    "attendanceCount", 
    "error", 
    "saveButton", 
    "attendanceRow", 
    "attendanceFields", 
    "validationBlock", 
    "validationMessage",
    "removeNotificationPanel",
    "removeNotificationMessage",
    "errorPanel"
  ]

  static values = {
    id: String,
    showInPerson: Boolean,
    showRemote: Boolean,
    allowsInPerson: Boolean,
    allowsRemote: Boolean,
    maxInPerson: Number,
    maxRemote: Number,
    currentInPerson: Number,
    currentRemote: Number
  }

  connect() {
    this.toggleSaveButtonState();
    
    if (this.hasRemoveNotificationPanelTarget && this.hasRemoveNotificationMessageTarget) {
      if (!this.removeNotificationPanelTarget.hidden && this.removeNotificationMessageTarget.textContent.trim()) {
        if (this.notificationTimer) {
          clearTimeout(this.notificationTimer)
        }
        this.notificationTimer = setTimeout(() => {
          this.hideRemoveNotification()
        }, 5000)
      }
    }
  }

  async showQuickLook(event) {
    event.preventDefault();

    const sessionId = event.currentTarget.getAttribute("data-driving-school-classroom-introductory-meeting-session-id");
    
    const quickViewElement = document.querySelector("#driving-school-classroom-introductory-meeting-session-quick-look");
    if (quickViewElement) {
      quickViewElement.classList.remove("u-hidden");
    }
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    try {
      const response = await fetch(`/schools/classroom/introductory-meetings/${sessionId}/quick-look`, {
        method: "GET",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        }
      });
      
      if (response.ok) {
        const responseBody = await response.text();
        Turbo.renderStreamMessage(responseBody);
      }
    } catch (error) {
      console.error("Error loading quick look:", error);
    }
  }
  
  closeQuickLook(event) {
    event.preventDefault();
    
    const quickViewElement = document.querySelector("#driving-school-classroom-introductory-meeting-session-quick-look");

    if (quickViewElement) {
      quickViewElement.classList.add("u-hidden");
    }
    
    const turboFrame = document.querySelector("#school_classroom_introductory_meeting_session_quick_look");
    if (turboFrame) {
      turboFrame.innerHTML = "";
    }
  }

  remove(event) {
    event.preventDefault();

    const pupilId = event.currentTarget.getAttribute('data-pupil-id');
    const attendanceId = event.currentTarget.getAttribute('data-attendance-id');
    const sessionId = this.idValue || this.element.getAttribute('data-driving-school-classroom-introductory-meeting-session-id-value');

    this.hideRemoveNotification();

    fetch(`/schools/classroom/introductory-meetings/${sessionId}/quick-look/remove/${pupilId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: JSON.stringify({ attendance_id: attendanceId })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.text()
    })
    .then(html => {
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      console.error('Error:', error)
    })
  }

  hideRemoveNotification() {
    if (!this.hasRemoveNotificationPanelTarget) return
    
    if (this.notificationTimer) {
      clearTimeout(this.notificationTimer)
      this.notificationTimer = null
    }
    
    this.removeNotificationPanelTarget.hidden = true
    this.removeNotificationPanelTarget.classList.add('u-hidden')
  }

  saveEnrollments(event) {
    event.preventDefault();
    
    this.hideValidationBlock();
    
    const validationErrors = this.validate();
    
    if (validationErrors.length > 0) {
      this.showValidationBlock(validationErrors.join("<br>"))
      return
    }
    
    const sessionId = this.idValue || this.element.getAttribute('data-driving-school-classroom-introductory-meeting-session-id-value')
    
    const attendances = []
    const checkedCheckboxes = Array.from(this.checkboxTargets).filter(cb => cb.checked)
    
    checkedCheckboxes.forEach(checkbox => {
      const pupilId = checkbox.dataset.pupilId || checkbox.value
      if (!pupilId) return
      
      const pupilRow = checkbox.closest('[data-driving-school-classroom-introductory-meeting-session-target="pupilRow"]')
      if (!pupilRow) return
      
      const attendanceTypeInput = pupilRow.querySelector(`input[name="attendance_type_${pupilId}"]:checked`)
      const attendanceType = attendanceTypeInput ? attendanceTypeInput.value : 'in_person'
      
      const numberOfAttendeesInput = pupilRow.querySelector(`#number_of_attendees_${pupilId}`)
      const numberOfAttendees = numberOfAttendeesInput ? parseInt(numberOfAttendeesInput.value, 10) : 1
      
      attendances.push({
        pupil_id: pupilId,
        attendance_type: attendanceType,
        expected_attendees_count: numberOfAttendees
      })
    })

    if (this.hasAttendanceRowTarget) {
      const existingRows = Array.from(this.attendanceRowTargets)
      existingRows.forEach(row => {
        const pupilId = row.dataset.pupilId || row.id.replace('pupil-row-', '')
        if (!pupilId) return
        
        if (attendances.find(a => a.pupil_id.toString() === pupilId.toString())) return
        
        const attendanceType = row.dataset.attendanceType || 'in_person'
        const expectedCount = parseInt(row.dataset.expectedAttendeesCount || '1', 10) || 1
        
        attendances.push({
          pupil_id: pupilId,
          attendance_type: attendanceType,
          expected_attendees_count: expectedCount
        })
      })
    }

    event.target.disabled = true
    
    fetch(`/schools/classroom/introductory-meetings/${sessionId}/quick-look`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: JSON.stringify({ attendances: attendances }),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.text()
    })
    .then(html => {
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      this.errorTarget.hidden = false
      console.error('Error:', error)
      this.saveButtonTarget.disabled = false
    })
  }

  toggleEnrollment(event) {
    const checkbox = event.target
    const pupilRow = checkbox.closest('[data-driving-school-classroom-introductory-meeting-session-target="pupilRow"]')
    
    if (!pupilRow) {
      console.error('Could not find pupil row', checkbox)
      return
    }

    const pupilId = checkbox.dataset.pupilId || pupilRow.id.replace('pupil-row-', '')
    
    const attendanceFields = pupilRow.querySelector(
      `[data-driving-school-classroom-introductory-meeting-session-target="attendanceFields"][data-pupil-id="${pupilId}"]`
    )
    
    if (checkbox.checked) {
      if (attendanceFields) {
        attendanceFields.classList.remove('u-hidden')
        attendanceFields.hidden = false
      }
    } else {
      if (attendanceFields) {
        attendanceFields.classList.add('u-hidden')
        attendanceFields.hidden = true
        
        const inPersonInput = attendanceFields.querySelector(`input[name="attendance_type_${pupilId}"][value="in_person"]`)
        const remoteInput = attendanceFields.querySelector(`input[name="attendance_type_${pupilId}"][value="remote"]`)
        
        if (inPersonInput) {
          inPersonInput.checked = true
        }
        if (remoteInput) {
          remoteInput.checked = false
        }
        
        const numberOfAttendeesInput = attendanceFields.querySelector(`#number_of_attendees_${pupilId}`)
        if (numberOfAttendeesInput) {
          numberOfAttendeesInput.value = '1'
        }
      }
    }
    
    this.toggleSaveButtonState()
  }

  toggleSaveButtonState() {
    if (!this.hasSaveButtonTarget) return
    
    const checkedCheckboxes = Array.from(this.checkboxTargets).filter(cb => cb.checked)
    this.saveButtonTarget.disabled = checkedCheckboxes.length === 0
  }

  updateAttendanceCount(change) {
    if (this.hasAttendanceCountTarget) {
      const currentCount = parseInt(this.attendanceCountTarget.textContent, 10)
      const newCount = currentCount + change
      this.attendanceCountTarget.textContent = newCount
    }
  }

  addCheckboxToRow(row, checkboxId, checkboxValue) {
    const label = document.createElement('label')
    label.className = 'Input--checkbox-label media m0'
    label.setAttribute('for', checkboxId)

    const input = document.createElement('input')
    input.type = 'checkbox'
    input.id = checkboxId
    input.name = 'driving_school_classroom_session_attendances[]'
    input.value = checkboxValue
    input.setAttribute('data-action', 'change->driving-school-classroom-multi-enroll#toggleEnrollment')
    input.setAttribute('data-driving-school-classroom-multi-enroll-target', 'checkbox')

    const span = document.createElement('span')
    span.className = 'Input--checkbox-custom'

    label.appendChild(input)
    label.appendChild(span)

    row.querySelector('.u-alignMiddle').appendChild(label)
  }

  validate() {
    const errors = [];
    const checkedCheckboxes = Array.from(this.checkboxTargets).filter(cb => cb.checked);
    
    let projectedInPersonTotal = this.hasCurrentInPersonValue ? this.currentInPersonValue : 0;
    let projectedRemoteTotal = this.hasCurrentRemoteValue ? this.currentRemoteValue : 0;
    
    checkedCheckboxes.forEach(checkbox => {
      const pupilId = checkbox.dataset.pupilId || checkbox.value;
      if (!pupilId) return;
      
      const pupilRow = checkbox.closest('[data-driving-school-classroom-introductory-meeting-session-target="pupilRow"]');
      if (!pupilRow) return;
      
      const pupilName = pupilRow.querySelector('.u-medium.u-block')?.textContent.trim() || 'Student';
      
      const attendanceTypeInput = pupilRow.querySelector(`input[name="attendance_type_${pupilId}"]:checked`);
      if (!attendanceTypeInput) {
        errors.push(`<strong class="u-textRedMid">${pupilName}</strong>: Please select how they will attend (in-person or remote).`);
        return;
      }
      
      const attendanceType = attendanceTypeInput.value;
      
      if (attendanceType === 'in_person' && !this.allowsInPersonValue) {
        errors.push(`<strong class="u-textRedMid">${pupilName}</strong>: In-person attendance is not available for this meeting session.`);
        return;
      }
      
      if (attendanceType === 'remote' && !this.allowsRemoteValue) {
        errors.push(`<strong class="u-textRedMid">${pupilName}</strong>: Remote attendance is not available for this meeting session.`);
        return;
      }
      
      const numberOfAttendeesInput = pupilRow.querySelector(`#number_of_attendees_${pupilId}`);
      if (!numberOfAttendeesInput || !numberOfAttendeesInput.value || parseInt(numberOfAttendeesInput.value, 10) < 1) {
        errors.push(`<strong class="u-textRedMid">${pupilName}</strong>: Please select how many will attend.`);
        return;
      }
      
      const numberOfAttendees = parseInt(numberOfAttendeesInput.value, 10);
      
      if (attendanceType === 'in_person') {
        projectedInPersonTotal += numberOfAttendees;
        
        if (this.hasMaxInPersonValue && this.maxInPersonValue > 0) {
          if (projectedInPersonTotal > this.maxInPersonValue) {
            const currentCount = this.hasCurrentInPersonValue ? this.currentInPersonValue : 0;
            errors.push(`<strong class="u-textRedMid">${pupilName}</strong>: Adding ${numberOfAttendees} in-person ${numberOfAttendees === 1 ? 'attendee' : 'attendees'} would exceed the maximum of ${this.maxInPersonValue} in-person attendees (currently ${currentCount} expected, would be ${projectedInPersonTotal}).`);
          }
        }
      } else if (attendanceType === 'remote') {
        projectedRemoteTotal += numberOfAttendees;
        
        if (this.hasMaxRemoteValue && this.maxRemoteValue > 0) {
          if (projectedRemoteTotal > this.maxRemoteValue) {
            const currentCount = this.hasCurrentRemoteValue ? this.currentRemoteValue : 0;
            errors.push(`<strong class="u-textRedMid">${pupilName}</strong>: Adding ${numberOfAttendees} remote ${numberOfAttendees === 1 ? 'attendee' : 'attendees'} would exceed the maximum of ${this.maxRemoteValue} remote attendees (currently ${currentCount} expected, would be ${projectedRemoteTotal}).`);
          }
        }
      }
    })
    
    return errors;
  }

  showValidationBlock(message) {
    if (this.hasValidationMessageTarget) {
      this.validationMessageTarget.innerHTML = message;
    }
    if (this.hasValidationBlockTarget) {
      this.validationBlockTarget.hidden = false;
      this.validationBlockTarget.classList.remove('u-hidden');
    }
  }

  hideValidationBlock() {
    if (this.hasValidationBlockTarget) {
      this.validationBlockTarget.hidden = true;
      this.validationBlockTarget.classList.add('u-hidden');
    }
  }
}
